import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Slide,
} from '@mui/material';
import { Close, Science } from '@mui/icons-material';
import {
  enableBetaExperience,
  redirectToOpenWebUIIfEnabled,
  isOpenWebUIAvailable,
} from '../functions/betaExperience';
import { useGlobalOptions } from '../hooks/useGlobalOptions';

interface BetaBannerProps {
  onDismiss?: () => void;
}

export const BetaBanner: React.FC<BetaBannerProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const { globalOptions } = useGlobalOptions();

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const handleTryNow = () => {
    setIsRedirecting(true);
    enableBetaExperience();
    // Small delay to show the redirecting state
    setTimeout(() => {
      redirectToOpenWebUIIfEnabled(globalOptions.openwebui_url);
    }, 1000);
  };

  // Don't show banner if OpenWebUI is not available
  if (!isOpenWebUIAvailable(globalOptions.openwebui_url)) {
    return null;
  }

  return (
    <Slide direction="down" in={isVisible} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: (theme) => 
            theme.palette.mode === 'dark' 
              ? 'rgba(25, 118, 210, 0.95)' 
              : 'rgba(33, 150, 243, 0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: (theme) => theme.shadows[4],
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 16px',
            maxWidth: '100%',
            margin: '0 auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Science sx={{ color: 'white' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontWeight: 500,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {isRedirecting 
                ? 'Redirecting to beta experience...'
                : 'Try our new beta chat experience with enhanced features!'
              }
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isRedirecting && (
              <Button
                variant="contained"
                size="small"
                onClick={handleTryNow}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: 'auto',
                  padding: '6px 12px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                Try Now
              </Button>
            )}

            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                color: 'white',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Slide>
  );
}; 